import React, { useState, useRef } from 'react';
import MandatoEditor from './MandatoEditor';

function UploadForm({ onSubmit, loading }) {
  const [saeFile, setSaeFile] = useState(null);
  const [vbFile, setVbFile] = useState(null);
  const [facturaFile, setFacturaFile] = useState(null);
  const [consecutivo, setConsecutivo] = useState('');
  const [showSAE, setShowSAE] = useState(true);
  const [showVB, setShowVB] = useState(false);
  const [showFactura, setShowFactura] = useState(false);
  const saeRef = useRef();
  const vbRef = useRef();
  const facturaRef = useRef();

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!saeFile || !facturaFile || !consecutivo) return;
    onSubmit(saeFile, vbFile, facturaFile, consecutivo || null);
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gold-700">CARGA DE DOCUMENTOS</h2>
          <MandatoEditor />
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* SAE PDF - collapsible */}
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            <button
              type="button"
              onClick={() => setShowSAE(!showSAE)}
              className="w-full flex items-center justify-between px-5 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <span className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                PDF SAE
                <span className="text-xs font-normal text-red-500">* Obligatorio</span>
              </span>
              <svg
                className={`w-5 h-5 text-gray-400 transition-transform ${showSAE ? 'rotate-180' : ''}`}
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {showSAE && (
              <div className="p-5 border-t border-gray-200">
                <p className="text-xs text-gray-500 mb-3">
                  Cargue el archivo PDF de la solicitud de autorización de embarque para validar la información.
                </p>
                <div
                  className={`border-2 border-dashed rounded-xl p-5 text-center cursor-pointer transition-colors
                    ${saeFile ? 'border-green-400 bg-green-50' : 'border-gray-300 hover:border-gold-400 hover:bg-gold-50'}`}
                  onClick={() => saeRef.current?.click()}
                >
                  <input
                    ref={saeRef}
                    type="file"
                    accept=".pdf"
                    className="hidden"
                    onChange={(e) => setSaeFile(e.target.files[0] || null)}
                  />
                  {saeFile ? (
                    <div className="flex items-center justify-center gap-2">
                      <span className="text-green-700 font-medium text-sm">{saeFile.name}</span>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setSaeFile(null); saeRef.current.value = ''; }}
                        className="ml-2 text-red-400 hover:text-red-600 text-xs"
                      >
                        Eliminar
                      </button>
                    </div>
                  ) : (
                    <div>
                      <p className="text-gray-500 text-xs">Haz clic para cargar el PDF de la SAE</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Consecutivo (obligatorio) */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Número de factura
              <span className="ml-2 text-xs font-normal text-red-500">* Obligatorio</span>
            </label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              required
              value={consecutivo}
              onChange={(e) => setConsecutivo(e.target.value.replace(/\D/g, ''))}
              placeholder="Ej: 191"
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-gold-400 focus:border-gold-400 outline-none"
            />
          </div>

          {/* Factura de Exportación - collapsible (obligatorio) */}
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            <button
              type="button"
              onClick={() => setShowFactura(!showFactura)}
              className="w-full flex items-center justify-between px-5 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <span className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                Factura de exportación
                <span className="text-xs font-normal text-red-500">* Obligatorio</span>
              </span>
              <svg
                className={`w-5 h-5 text-gray-400 transition-transform ${showFactura ? 'rotate-180' : ''}`}
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {showFactura && (
              <div className="p-5 border-t border-gray-200">
                <p className="text-xs text-gray-500 mb-3">
                  Cargue la factura de exportación.
                </p>
                <div
                  className={`border-2 border-dashed rounded-xl p-5 text-center cursor-pointer transition-colors
                    ${facturaFile ? 'border-green-400 bg-green-50' : 'border-gray-300 hover:border-gold-400 hover:bg-gold-50'}`}
                  onClick={() => facturaRef.current?.click()}
                >
                  <input
                    ref={facturaRef}
                    type="file"
                    accept=".pdf"
                    className="hidden"
                    onChange={(e) => setFacturaFile(e.target.files[0] || null)}
                  />
                  {facturaFile ? (
                    <div className="flex items-center justify-center gap-2">
                      <span className="text-green-700 font-medium text-sm">{facturaFile.name}</span>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setFacturaFile(null); facturaRef.current.value = ''; }}
                        className="ml-2 text-red-400 hover:text-red-600 text-xs"
                      >
                        Eliminar
                      </button>
                    </div>
                  ) : (
                    <div>
                      <p className="text-gray-500 text-xs">Haz clic para cargar el PDF de la factura de exportación</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Visto Bueno - collapsible */}
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            <button
              type="button"
              onClick={() => setShowVB(!showVB)}
              className="w-full flex items-center justify-between px-5 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <span className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                Visto bueno aprobado (ANM)
                <span className="text-xs font-normal text-gray-400">— Opcional</span>
              </span>
              <svg
                className={`w-5 h-5 text-gray-400 transition-transform ${showVB ? 'rotate-180' : ''}`}
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {showVB && (
              <div className="p-5 border-t border-gray-200">
                <p className="text-xs text-gray-500 mb-3">
                  Cargue el visto bueno aprobado del Ministerio de Comercio, Industria y Turismo.
                </p>
                <div
                  className={`border-2 border-dashed rounded-xl p-5 text-center cursor-pointer transition-colors
                    ${vbFile ? 'border-green-400 bg-green-50' : 'border-gray-300 hover:border-gold-400 hover:bg-gold-50'}`}
                  onClick={() => vbRef.current?.click()}
                >
                  <input
                    ref={vbRef}
                    type="file"
                    accept=".pdf"
                    className="hidden"
                    onChange={(e) => setVbFile(e.target.files[0] || null)}
                  />
                  {vbFile ? (
                    <div className="flex items-center justify-center gap-2">
                      <span className="text-green-700 font-medium text-sm">{vbFile.name}</span>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setVbFile(null); vbRef.current.value = ''; }}
                        className="ml-2 text-red-400 hover:text-red-600 text-xs"
                      >
                        Eliminar
                      </button>
                    </div>
                  ) : (
                    <div>
                      <p className="text-gray-500 text-xs">Haz clic para cargar el PDF del Visto Bueno</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={!saeFile || !facturaFile || loading}
            className={`w-full py-3 rounded-xl font-bold text-white text-lg transition-all
              ${!saeFile || !facturaFile || loading
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-gradient-to-r from-gold-600 to-gold-400 hover:from-gold-700 hover:to-gold-500 shadow-md hover:shadow-lg'
              }`}
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                </svg>
                Procesando...
              </span>
            ) : (
              '🔍 Validar SAE'
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

export default UploadForm;
